/* ══════════════════════════════════════════════════════════════
   src/content/classes.ts
   All static content for the Classes page.
   Plain strings only — no JSX — so this file can be translated.
   ══════════════════════════════════════════════════════════════ */

import { hfClasses } from './classes-hf';

export interface ClassFeature {
  name?: string;
  description?: string;
}

export interface MartialStance {
  name: string;
  description: string;
}

export interface MartialFormData {
  title?: string;
  description: string;
  tiers: Record<string, MartialStance[]>;
}

export interface Subclass {
  name: string;
  spellcastTrait?: string;
  description?: string[];
  foundation?: Array<ClassFeature | string>;
  specialization?: Array<ClassFeature | string>;
  mastery?: Array<ClassFeature | string>;
  martialForm?: MartialFormData;
}

export interface ClassStats {
  domains?: string;
  evasion?: string | number;
  hp?: string | number;
  stress?: string | number;
  suggestedPrimary?: string | string[];
  suggestedSecondary?: string | string[];
  suggestedArmor?: string;
  suggestedTraits?: string;
}

export interface ClassInventory {
  take: string;
  chooseBetween: string[];
  andEither: string[];
  thenDecideWhatYouUseForYourCraft?: string[];
  /** Guide heading for the craft line; the guides default to "Then decide what you carry your spells in". */
  craftLabel?: string;
}

export interface CharacterDescription {
  clothes: string[];
  eyes: string[];
  body: string[];
  skin: string[];
  attitude: string[];
}

export interface DhClass {
  name: string;
  /** The one-line pitch at the top of the class's character guide. HF classes use description[0]. */
  tagline?: string;
  source?: 'Void' | 'HF';
  description?: string[];
  items?: string;
  inventory?: ClassInventory;
  characterDescription?: CharacterDescription;
  hopeFeature?: string;
  classFeature?: string;
  classFeatures?: Array<ClassFeature | string>;
  stats?: ClassStats;
  subclasses?: Record<string, Subclass>;
  backgroundQuestions?: string[];
  connectionQuestions?: string[];
}

export const classes: DhClass[] = [
  {
    name: 'Bard',
    tagline: 'As a bard, you know how to get people to talk, bring attention to yourself, and use words or music to influence the world around you.',
    description: [
      "Bards are the most charismatic people in all the realms. Members of this class are masters of captivation and specialize in a variety of performance types, including singing, playing musical instruments, weaving tales, or telling jokes. Whether performing for an audience or speaking to an individual, bards thrive in social situations.",
      "Members of this profession bond and train at schools or guilds, but a current of egotism runs through those of the bardic persuasion. While they may be the most likely class to bring people together, a bard of ill temper can just as easily tear a party apart.",
    ],
    stats: {
      domains: 'Grace & Codex',
      evasion: 10,
      hp: 5,
      suggestedTraits: '0, -1, +1, 0, +2, +1',
      suggestedPrimary: 'Rapier',
      suggestedSecondary: 'Small Dagger',
      suggestedArmor: 'Gambeson Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a romance novel', 'a letter never opened'],
      thenDecideWhatYouUseForYourCraft: ['songbook', 'journal', 'etc.'],
    },
    characterDescription: {
      clothes: ['extravagant', 'fancy', 'loud', 'oversized', 'ragged', 'sleek', 'wild'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a barkeep', 'a magician', 'a ringmaster', 'a rock star', 'a swashbuckler'],
    },
    hopeFeature: 'Make a Scene: Spend 3 Hope to temporarily Distract a target within Close range, giving them a -2 penalty to their Difficulty.',
    classFeatures: [
      {
        name: 'Rally',
        description: 'Once per session, describe how you rally the party and give yourself and each of your allies a Rally Die. At level 1, your Rally Die is a d6. A PC can spend their Rally Die to roll it, adding the result to their action roll, reaction roll, damage roll, or to clear a number of Stress equal to the result. At the end of each session, clear all unspent Rally Dice. At level 5, your Rally Die increases to a d8.',
      },
    ],
    subclasses: {
      Troubadour: {
        name: 'Troubadour',
        spellcastTrait: 'Presence',
        foundation: [
          {
            name: 'Gifted Performer',
            description: 'You can play three different types of songs, once each per long rest; describe how you perform for others to gain the listed benefit. Relaxing Song: You and all allies within Close range clear a Hit Point. Epic Song: Make a target within Close range temporarily Vulnerable. Heartbreaking Song: You and all allies within Close range gain a Hope.',
          },
        ],
        specialization: [
          {
            name: 'Maestro',
            description: 'Your rallying songs steel the courage of those who listen. When you give a Rally Die to an ally, they can gain a Hope or clear a Stress.',
          },
        ],
        mastery: [
          {
            name: 'Virtuoso',
            description: 'Your Rally Die increases to a d10. You are among the greatest of your craft. Each of your Gifted Performer songs can be performed twice per long rest.',
          },
        ],
      },
      Wordsmith: {
        name: 'Wordsmith',
        spellcastTrait: 'Presence',
        foundation: [
          {
            name: 'Rousing Speech',
            description: 'Once per long rest, you can give a heartfelt, inspiring speech. All allies within Far range clear 2 Stress.',
          },
          {
            name: 'Heart of a Poet',
            description: 'After you make an action roll to impress, persuade, or offend someone, you can spend a Hope to add a d4 to the roll.',
          },
        ],
        specialization: [
          {
            name: 'Eloquent',
            description: 'Your moving words boost morale. Once per session, when you encourage an ally, you can do one of the following: Allow them to find a mundane object or tool they need. Help an Ally without spending Hope. Give them an additional downtime move during their next rest.',
          },
        ],
        mastery: [
          {
            name: 'Epic Poetry',
            description: 'Your Rally Die increases to a d10. Additionally, when you Help an Ally, you can narrate the moment as if writing the tale of their heroism in a memoir. When you do, roll a d10 as your advantage die.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "Who from your community taught you to have such confidence in yourself?",
      "You were in love once. Who did you adore, and how did they hurt you?",
      "You've always looked up to another bard. Who are they, and why do you idolize them?",
    ],
    connectionQuestions: [
      "What made you realize we were going to be such good friends?",
      "What do I do that annoys you?",
      "Why do you grab my hand at night?",
    ],
  },
  {
    name: 'Druid',
    tagline: 'As a druid, you are a force of nature, preserving the balance of life and death by channeling the wilds themselves through you.',
    description: [
      "Becoming a druid is more than an occupation; it's a calling for those who wish to learn from and protect the magic of the wilderness. While one might underestimate a gentle druid who practices the often-quiet work of cultivating flora, druids who channel the untamed forces of nature are terrifying to behold.",
      "Druids cultivate their abilities in small groups, often connected by a specific ethos or locale, but some choose to work alone. Through years of study and dedication, druids can learn to transform into beasts and shape nature itself.",
    ],
    stats: {
      domains: 'Sage & Arcana',
      evasion: 10,
      hp: 6,
      suggestedTraits: '+1, 0, +1, +2, -1, 0',
      suggestedPrimary: 'Shortstaff',
      suggestedSecondary: 'Round Shield',
      suggestedArmor: 'Leather Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a small bag of rocks and bones', 'a strange pendant found in the dirt'],
    },
    characterDescription: {
      clothes: ['camouflaged', 'grown', 'loose', 'natural', 'patchwork', 'regal', 'scraps'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a firecracker', 'a fox', 'a guide', 'a hippie', 'a witch'],
    },
    hopeFeature: 'Evolution: Spend 3 Hope to transform into a Beastform without marking a Stress. When you do, choose one trait to raise by +1 until you drop out of that Beastform.',
    classFeatures: [
      {
        name: 'Beastform',
        description: "Mark a Stress to magically transform into a creature of your tier or lower from the Beastform list. You can drop out of this form at any time. While transformed, you can't use weapons or cast spells from domain cards, but you can still use other features or abilities you have access to. Spells you cast before you transform stay active and last for their normal duration, and you can talk and communicate as normal. Additionally, you gain the Beastform's features, add their Evasion bonus to your Evasion, and use the trait specified in their statistics for your attack. While in a Beastform, your armor becomes part of your body and you mark Armor Slots as usual; when you drop out, those marked slots remain marked. If you mark your last Hit Point, you automatically drop out of this form.",
      },
      {
        name: 'Wildtouch',
        description: 'You can perform harmless, subtle effects that involve nature, such as causing a flower to rapidly grow, summoning a slight gust of wind, or starting a campfire, at will.',
      },
    ],
    subclasses: {
      'Warden of the Elements': {
        name: 'Warden of the Elements',
        spellcastTrait: 'Instinct',
        foundation: [
          {
            name: 'Elemental Incarnation',
            description: 'Mark a Stress to Channel one of the following elements until you take Severe damage or until your next rest:\n• Fire: When an adversary within Melee range deals damage to you, they take 1d10 magic damage.\n• Earth: Gain a bonus to your damage thresholds equal to your Proficiency.\n• Water: When you deal damage to an adversary within Melee range, all other adversaries within Very Close range must mark a Stress.\n• Air: You can hover, gaining advantage on Agility Rolls.',
          },
        ],
        specialization: [
          {
            name: 'Elemental Aura',
            description: 'Once per rest while Channeling, you can assume an aura matching your element affecting targets within Close range.\n• Fire: When an adversary marks 1 or more Hit Points, they must also mark a Stress.\n• Earth: Your allies gain a +1 bonus to Strength.\n• Water: When an adversary deals damage to you, mark a Stress to move them anywhere within Very Close range.\n• Air: When you or an ally takes damage from beyond Melee range, reduce the damage by 1d8.',
          },
        ],
        mastery: [
          {
            name: 'Elemental Dominion',
            description: 'While Channeling, you gain an additional benefit per element:\n• Fire: Gain +1 to Proficiency for attacks and spells that deal damage.\n• Earth: When you would mark Hit Points, roll a d6 per Hit Point marked; each result of 6 reduces the Hit Points marked by 1.\n• Water: When an attack against you succeeds, mark a Stress to make the attacker temporarily Vulnerable.\n• Air: Gain +1 to Evasion and you can fly.',
          },
        ],
      },
      'Warden of Renewal': {
        name: 'Warden of Renewal',
        spellcastTrait: 'Instinct',
        foundation: [
          {
            name: 'Clarity of Nature',
            description: "Once per long rest, create a space of natural serenity within Close range. When you spend a few minutes resting within the space, clear Stress equal to your Instinct, distributed as you choose between you and your allies.",
          },
          {
            name: 'Regeneration',
            description: 'Touch a creature and spend 3 Hope. That creature clears 1d4 Hit Points.',
          },
        ],
        specialization: [
          {
            name: 'Regenerative Reach',
            description: 'You can target creatures within Very Close range with your Regeneration feature.',
          },
          {
            name: "Warden's Protection",
            description: 'Once per long rest, spend 2 Hope to clear 2 Hit Points on 1d4 allies within Close range.',
          },
        ],
        mastery: [
          {
            name: 'Defender',
            description: "When you're in Beastform and an ally within Close range marks 2 or more Hit Points, you can mark a Stress to reduce the number of Hit Points they mark by 1.",
          },
        ],
      },
    },
    backgroundQuestions: [
      "Why was the community you grew up in so reliant on nature and its creatures?",
      "Who was the first wild animal you bonded with? Why did your bond end?",
      "Who has been trying to hunt you down? What do they want from you?",
    ],
    connectionQuestions: [
      "What did you confide in me that makes me leap into danger for you every time?",
      "What animal do I say you remind me of?",
      "What affectionate nickname have you given me?",
    ],
  },
  {
    name: 'Guardian',
    tagline: 'As a guardian, you run into danger to protect your party, keeping watch over those who might not survive without you there.',
    description: [
      "The title of guardian represents an array of martial professions, speaking more to their moral compass and unshakeable fortitude than the means by which they fight. While many guardians join groups of militants for either a country or cause, they're more likely to follow those few they truly care for, majority be damned.",
      "Guardians are known for fighting with remarkable ferocity even against overwhelming odds, defending their cohort above all else. Woe betide those who harm the ally of a guardian, as the guardian will answer this injury in kind.",
    ],
    stats: {
      domains: 'Valor & Blade',
      evasion: 9,
      hp: 7,
      suggestedTraits: '+1, +2, -1, 0, +1, 0',
      suggestedPrimary: 'Battleaxe',
      suggestedSecondary: null as unknown as string,
      suggestedArmor: 'Chainmail Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a totem from your mentor', 'a secret key'],
    },
    characterDescription: {
      clothes: ['casual', 'intricate', 'loose', 'padded', 'royal', 'tactical', 'weathered'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a captain', 'a caretaker', 'an elephant', 'a general', 'a wrestler'],
    },
    hopeFeature: 'Frontline Tank: Spend 3 Hope to clear 2 Armor Slots.',
    classFeatures: [
      {
        name: 'Unstoppable',
        description: "Once per long rest, you can become Unstoppable. You gain an Unstoppable Die (d4 at level 1, d6 at level 5). Place it on your character sheet starting with the 1 value facing up. After you make a damage roll that deals 1 or more Hit Points to a target, increase the die value by one. When the value would exceed its maximum or the scene ends, remove the die and drop out of Unstoppable. While Unstoppable: reduce the severity of physical damage by one threshold; add the current die value to your damage roll; you can't be Restrained or Vulnerable.",
      },
    ],
    subclasses: {
      Stalwart: {
        name: 'Stalwart',
        foundation: [
          {
            name: 'Unwavering',
            description: 'You gain a permanent +1 bonus to all damage thresholds.',
          },
          {
            name: 'Iron Will',
            description: 'When you take physical damage, you can mark an additional Armor Slot to reduce the severity of the damage by one threshold.',
          },
        ],
        specialization: [
          {
            name: 'Unrelenting',
            description: 'You gain a permanent +2 bonus to all damage thresholds.',
          },
          {
            name: 'Partners-in-Arms',
            description: 'When an ally within Very Close range takes damage, you can mark an Armor Slot to reduce the severity of the damage by one threshold.',
          },
        ],
        mastery: [
          {
            name: 'Undaunted',
            description: 'You gain a permanent +3 bonus to all damage thresholds.',
          },
          {
            name: 'Loyal Protector',
            description: 'When an ally within Close range has 2 or fewer Hit Points remaining and would take damage, you can mark a Stress to sprint to their side and take the damage instead.',
          },
        ],
      },
      Vengeance: {
        name: 'Vengeance',
        foundation: [
          {
            name: 'At Ease',
            description: 'You gain an additional Stress slot.',
          },
          {
            name: 'Revenge',
            description: 'When an adversary within Melee range succeeds on an attack against you, you can mark 2 Stress to force the attacker to mark a Hit Point.',
          },
        ],
        specialization: [
          {
            name: 'Act of Reprisal',
            description: 'When an adversary damages an ally within Melee range of you, gain a +1 bonus to your Proficiency for your next successful attack against that adversary.',
          },
        ],
        mastery: [
          {
            name: 'Nemesis',
            description: 'Spend 2 Hope to Prioritize an adversary until your next rest. When making an attack against your Prioritized adversary, you can swap the results of your Hope and Fear Dice. Only one adversary can be Prioritized at a time.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "Who from your community did you fail to protect, and why do you still think of them?",
      "You've been tasked with protecting something important and delivering it somewhere dangerous. What is it, and where does it need to go?",
      "You consider an aspect of yourself to be a weakness. What is it, and how has it affected you?",
    ],
    connectionQuestions: [
      "How did I save your life the first time we met?",
      "What small gift did you give me that you notice I always carry with me?",
      "What lie have you told me about yourself that I absolutely believe?",
    ],
  },
  {
    name: 'Ranger',
    tagline: 'As a ranger, your keen eyes and graceful haste make you indispensable when tracking down enemies and navigating the wilds.',
    description: [
      "Rangers are highly skilled hunters who, despite their martial abilities, rarely lend their skills to an army. Through mastery of the body and a deep understanding of the wilderness, rangers become sly tacticians, pursuing their quarry with cunning and patience.",
      "Many rangers track and fight alongside an animal companion with whom they've forged a powerful spiritual bond. By honing their skills in the wild, rangers become expert trackers, as likely to ensnare their foes in a trap as they are to assail them head-on.",
    ],
    stats: {
      domains: 'Bone & Sage',
      evasion: 12,
      hp: 6,
      suggestedTraits: '+2, 0, +1, +1, -1, 0',
      suggestedPrimary: 'Shortbow',
      suggestedSecondary: null as unknown as string,
      suggestedArmor: 'Leather Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a trophy from your first kill', 'a seemingly broken compass'],
    },
    characterDescription: {
      clothes: ['flowing', 'muted', 'natural', 'stained', 'tactical', 'tight', 'woven'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a child', 'a ghost', 'a survivalist', 'a teacher', 'a watchdog'],
    },
    hopeFeature: 'Hold Them Off: Spend 3 Hope when you succeed on an attack with a weapon to use that same roll against two additional adversaries within range of the attack.',
    classFeatures: [
      {
        name: "Ranger's Focus",
        description: "Spend a Hope and make an attack against a target. On a success, deal your attack's normal damage and temporarily make the target your Focus. Until this feature ends or you make a different creature your Focus, you gain the following benefits against your Focus: You know precisely what direction they are in. When you deal damage to them, they must mark a Stress. When you fail an attack against them, you can end your Ranger's Focus to reroll your Duality Dice.",
      },
    ],
    subclasses: {
      Beastbound: {
        name: 'Beastbound',
        spellcastTrait: 'Agility',
        foundation: [
          {
            name: 'Companion',
            description: "You have a loyal animal companion. Take the Ranger Companion sheet. Your companion starts with Evasion 10 and two Experiences at +2. Their attack deals d6 physical damage at Melee range. Command your companion with a Spellcast Roll; spend Hope to add a Companion Experience to the roll. Your companion marks Stress instead of Hit Points; if their last Stress is marked they drop out and return at your next long rest with 1 Stress cleared.",
          },
        ],
        specialization: [
          {
            name: 'Expert Training',
            description: 'Choose an additional level-up option for your companion.',
          },
          {
            name: 'Battle-Bonded',
            description: "When an adversary attacks you while your companion is within their Melee range, gain +2 to your Evasion against that attack.",
          },
        ],
        mastery: [
          {
            name: 'Advanced Training',
            description: 'Choose two additional level-up options for your companion.',
          },
          {
            name: 'Loyal Friend',
            description: "Once per long rest, when damage would mark your companion's last Stress or your last Hit Point and you're within Close range of each other, you or your companion can rush to the other's side and take the damage instead.",
          },
        ],
      },
      Wayfinder: {
        name: 'Wayfinder',
        spellcastTrait: 'Agility',
        foundation: [
          {
            name: 'Ruthless Predator',
            description: 'When you make a damage roll, you can mark a Stress to gain +1 Proficiency for that roll. When you deal Severe damage to an adversary, they must mark a Stress.',
          },
          {
            name: 'Path Forward',
            description: "When traveling to a place you've previously been or carrying an object from that location, you can identify the shortest, most direct path to get there.",
          },
        ],
        specialization: [
          {
            name: 'Elusive Predator',
            description: 'When your Focus makes an attack against you, gain a +2 bonus to your Evasion against that attack.',
          },
        ],
        mastery: [
          {
            name: 'Apex Predator',
            description: "Before making an attack roll against your Focus, spend a Hope. On a successful attack, remove a Fear from the GM's Fear pool.",
          },
        ],
      },
    },
    backgroundQuestions: [
      "A terrible creature hurt your community, and you've vowed to hunt them down. What are they, and what unique trail or sign do they leave behind?",
      "Your first kill almost killed you, too. What was it, and what part of you was never the same after that event?",
      "You've traveled many dangerous lands, but what is the one place you refuse to go?",
    ],
    connectionQuestions: [
      "What friendly competition do we have?",
      "Why do you act differently when we're alone than when others are around?",
      "What threat have you asked me to watch for, and why are you worried about it?",
    ],
  },
  {
    name: 'Rogue',
    tagline: 'As a rogue, you have experience fighting with your blade as well as your wit, preferring to move quickly and fight quietly.',
    description: [
      "Rogues are scoundrels, often in both attitude and practice. Broadly known as liars and thieves, the best among this class move through the world anonymously. Utilizing their sharp wits and blades, rogues trick their foes through social manipulation as easily as breaking locks, climbing through windows, or dealing underhanded blows.",
      "These masters of magical craft manipulate shadow and movement, adding an array of useful and deadly tools to their repertoire. Rogues frequently establish guilds to meet future accomplices, hire out jobs, and hone secret skills, proving that there's honor among thieves for those who know where to look.",
    ],
    stats: {
      domains: 'Midnight & Grace',
      evasion: 12,
      hp: 6,
      suggestedTraits: '+1, -1, +2, 0, +1, 0',
      suggestedPrimary: 'Dagger',
      suggestedSecondary: 'Small Dagger',
      suggestedArmor: 'Gambeson Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a set of forgery tools', 'a grappling hook'],
    },
    characterDescription: {
      clothes: ['clean', 'dark', 'inconspicuous', 'leather', 'scary', 'tactical', 'tight'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a bandit', 'a con artist', 'a gambler', 'a mob boss', 'a pirate'],
    },
    hopeFeature: "Rogue's Dodge: Spend 3 Hope to gain a +2 bonus to your Evasion until the next time an attack succeeds against you. Otherwise, this bonus lasts until your next rest.",
    classFeatures: [
      {
        name: 'Cloaked',
        description: 'Any time you would be Hidden, you are instead Cloaked. In addition to the benefits of the Hidden condition, while Cloaked you remain unseen if you are stationary when an adversary moves to where they would normally see you. After you make an attack or end a move within line of sight of an adversary, you are no longer Cloaked.',
      },
      {
        name: 'Sneak Attack',
        description: 'When you succeed on an attack while Cloaked or while an ally is within Melee range of your target, add a number of d6s equal to your tier to your damage roll. (Tier 1 at level 1, Tier 2 at levels 2-4, Tier 3 at levels 5-7, Tier 4 at levels 8-10.)',
      },
    ],
    subclasses: {
      Nightwalker: {
        name: 'Nightwalker',
        spellcastTrait: 'Finesse',
        foundation: [
          {
            name: 'Shadow Stepper',
            description: 'Mark a Stress when moving into an area of darkness or a shadow to teleport to another shadow within Far range, arriving Cloaked.',
          },
        ],
        specialization: [
          {
            name: 'Dark Cloud',
            description: "Make a Spellcast Roll (15) to create a dark cloud covering an area within Close range. Those inside the cloud can't see out and those outside can't see in. You are Cloaked from adversaries whose line of sight is blocked by the cloud.",
          },
          {
            name: 'Adrenaline',
            description: 'While you are Vulnerable, add your level to all damage rolls.',
          },
        ],
        mastery: [
          {
            name: 'Fleeting Shadow',
            description: 'You gain a permanent +1 bonus to your Evasion. The range of Shadow Stepper extends to Very Far range.',
          },
          {
            name: 'Vanishing Act',
            description: 'Mark a Stress to become Cloaked at any time (this also automatically clears the Restrained condition). You remain Cloaked until you roll with Fear or until your next rest.',
          },
        ],
      },
      Syndicate: {
        name: 'Syndicate',
        spellcastTrait: 'Finesse',
        foundation: [
          {
            name: 'Well-Connected',
            description: "When you arrive in a prominent town or environment, you know somebody there. Give them a name, note their usefulness, and choose one fact: they owe you a favor (but are hard to find), they'll ask for something in exchange, they're always in trouble, you used to be together romantically, or you didn't part on great terms.",
          },
        ],
        specialization: [
          {
            name: 'Contacts Everywhere',
            description: 'Once per session, briefly call on a shady contact for one benefit: 1 handful of gold, a unique tool, or a mundane object; a +3 bonus to your next roll\'s Hope or Fear Die; or add 2d8 to your next damage roll.',
          },
        ],
        mastery: [
          {
            name: 'Reliable Backup',
            description: 'Contacts Everywhere can be used three times per session. Additional options: when you mark 1 or more Hit Points, a contact can reduce it by 1; when making a Presence Roll in conversation, you can roll a d20 as your Hope Die.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "What did you get caught doing that got you exiled from your home community?",
      "You used to have a different life, but you've tried to leave it behind. Who from your past is still chasing you?",
      "Who from your past were you most sad to say goodbye to?",
    ],
    connectionQuestions: [
      "What did I recently convince you to do that got us both in trouble?",
      "What have I discovered about your past that I hold secret from the others?",
      "Who do you know from my past, and how have they influenced your feelings about me?",
    ],
  },
  {
    name: 'Seraph',
    tagline: "As a seraph, you've taken a vow to a god who helps you channel sacred arcane power to keep your party on their feet.",
    description: [
      "Seraphs are divine fighters and healers imbued with sacred purpose. A wide array of deities exist within the realms, and thus numerous kinds of seraphs are appointed by these gods. Their ethos traditionally aligns with the domain or goals of their god, such as defending the weak, exacting vengeance, protecting a land or artifact, or upholding a particular faith.",
      "Some seraphs ally themselves with an army or locale, much to the satisfaction of their rulers, but other crusaders fight in opposition to the follies of the Mortal Realm. It is better to be a seraph's ally than their enemy, as they are terrifying foes to those who defy their purpose.",
    ],
    stats: {
      domains: 'Splendor & Valor',
      evasion: 9,
      hp: 7,
      suggestedTraits: '0, +2, 0, +1, +1, -1',
      suggestedPrimary: 'Hallowed Axe',
      suggestedSecondary: 'Round Shield',
      suggestedArmor: 'Chainmail Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a bundle of offerings', 'a sigil of your god'],
    },
    characterDescription: {
      clothes: ['glowing', 'rippling', 'ornate', 'tight', 'modest', 'strange', 'natural'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['an angel', 'a doctor', 'an evangelist', 'a monk', 'a priest'],
    },
    hopeFeature: 'Life Support: Spend 3 Hope to clear a Hit Point on an ally within Close range.',
    classFeatures: [
      {
        name: 'Prayer Dice',
        description: "At the beginning of each session, roll a number of d4s equal to your subclass's Spellcast trait and place them on your character sheet. These are your Prayer Dice. You can spend any number of Prayer Dice to aid yourself or an ally within Far range. Use a spent die's value to reduce incoming damage, add to a roll's result after the roll is made, or gain Hope equal to the result. At the end of each session, clear all unspent Prayer Dice.",
      },
    ],
    subclasses: {
      'Divine Wielder': {
        name: 'Divine Wielder',
        spellcastTrait: 'Strength',
        foundation: [
          {
            name: 'Spirit Weapon',
            description: 'An equipped Melee or Very Close weapon can fly to attack an adversary within Close range and return to you. Mark a Stress to target an additional adversary with the same attack.',
          },
          {
            name: 'Sparing Touch',
            description: 'Once per long rest, touch a creature to clear 2 Hit Points or 2 Stress.',
          },
        ],
        specialization: [
          {
            name: 'Devout',
            description: 'When rolling Prayer Dice at the start of a session, roll an additional die and discard the lowest result. Sparing Touch can now be used twice per long rest.',
          },
        ],
        mastery: [
          {
            name: 'Sacred Resonance',
            description: 'When you roll damage for Spirit Weapon, if any damage dice results match, double the value of each matching die (for example, two 5s become two 10s).',
          },
        ],
      },
      'Winged Sentinel': {
        name: 'Winged Sentinel',
        spellcastTrait: 'Strength',
        foundation: [
          {
            name: 'Wings of Light',
            description: 'You can fly. While flying, you can mark a Stress to carry another willing creature of approximately your size or smaller. While flying, spend a Hope to deal an extra 1d8 damage on a successful attack.',
          },
        ],
        specialization: [
          {
            name: 'Ethereal Visage',
            description: "While flying, you have advantage on Presence Rolls. When you succeed with Hope on a Presence Roll, you can remove a Fear from the GM's Fear pool instead of gaining Hope.",
          },
        ],
        mastery: [
          {
            name: 'Ascendant',
            description: 'You gain a permanent +4 bonus to your Severe damage threshold.',
          },
          {
            name: 'Power of the Gods',
            description: 'While flying, your Wings of Light feature deals an extra 1d12 damage instead of 1d8.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "Which god did you devote yourself to? What incredible feat did they perform for you in a moment of desperation?",
      "How did your appearance change after taking your oath?",
      "In what strange or unique way do you communicate with your god?",
    ],
    connectionQuestions: [
      "What promise did you make me agree to, should you die on the battlefield?",
      "Why do you ask me so many questions about my god?",
      "You've told me to protect one member of our party above all others, even yourself. Who are they and why?",
    ],
  },
  {
    name: 'Sorcerer',
    tagline: "As a sorcerer, you were born with innate magical power, and you've learned how to wield that power to get what you want.",
    description: [
      "Not all innate magic users choose to hone their craft, but those who do can become powerful sorcerers. The gifts of these wielders are passed down through families, even if the family is unaware of or reluctant to practice them. A sorcerer's abilities can range from the elemental to the illusionary and beyond, and many practitioners band together into collectives based on their talents.",
      "The act of becoming a formidable sorcerer is not the practice of acquiring power, but learning to cultivate and control the power one already possesses. The magic of a misguided or undisciplined sorcerer is a dangerous force indeed.",
    ],
    stats: {
      domains: 'Arcana & Midnight',
      evasion: 10,
      hp: 6,
      suggestedTraits: '0, -1, +1, +2, +1, 0',
      suggestedPrimary: 'Dualstaff',
      suggestedSecondary: null as unknown as string,
      suggestedArmor: 'Gambeson Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a whispering orb', 'a family heirloom'],
    },
    characterDescription: {
      clothes: ['always moving', 'flamboyant', 'inconspicuous', 'layered', 'ornate', 'tight'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a celebrity', 'a commander', 'a politician', 'a prankster', "a wolf in sheep's clothing"],
    },
    hopeFeature: 'Volatile Magic: Spend 3 Hope to reroll any number of your damage dice on an attack that deals magic damage.',
    classFeatures: [
      {
        name: 'Arcane Sense',
        description: 'You can sense the presence of magical people and objects within Close range.',
      },
      {
        name: 'Minor Illusion',
        description: 'Make a Spellcast Roll (10). On a success, you create a minor visual illusion no larger than yourself within Close range. This illusion is convincing to anyone at Close range or farther.',
      },
      {
        name: 'Channel Raw Power',
        description: "Once per long rest, you can place a domain card from your loadout into your vault and choose to either: Gain Hope equal to the level of the card, or enhance a spell that deals damage, gaining a bonus to your damage roll equal to twice the level of the card.",
      },
    ],
    subclasses: {
      'Elemental Origin': {
        name: 'Elemental Origin',
        spellcastTrait: 'Instinct',
        foundation: [
          {
            name: 'Elementalist',
            description: 'Choose one element: air, earth, fire, lightning, or water. You can shape it into harmless effects at will. Spend a Hope to help any action roll involving your element: gain a +2 bonus to the roll or a +3 bonus to damage.',
          },
        ],
        specialization: [
          {
            name: 'Natural Evasion',
            description: 'When an attack roll against you succeeds, you can mark a Stress to roll a d6 and add the result to your Evasion against that attack.',
          },
        ],
        mastery: [
          {
            name: 'Transcendence',
            description: 'Once per long rest, transform into a physical manifestation of your element. Until your next rest, choose two of the following benefits: +4 to your Severe threshold, +1 to a character trait, +1 to Proficiency, +2 to Evasion.',
          },
        ],
      },
      'Primal Origin': {
        name: 'Primal Origin',
        spellcastTrait: 'Instinct',
        foundation: [
          {
            name: 'Manipulate Magic',
            description: 'After casting a spell or making a magic damage attack, mark a Stress to do one of the following: extend the spell\'s reach by one range; gain +2 to the action roll\'s result; double a damage die of your choice; or hit an additional target within range.',
          },
        ],
        specialization: [
          {
            name: 'Enchanted Aid',
            description: "When you Help an Ally with a Spellcast Roll, roll a d8 as your advantage die. Once per long rest after an ally makes a Spellcast Roll with your help, you can swap the results of their Duality Dice.",
          },
        ],
        mastery: [
          {
            name: 'Arcane Charge',
            description: 'When you take magic damage, become Charged. Or spend 2 Hope to become Charged. When you successfully attack with magic damage while Charged, clear your Charge to either gain +10 to damage or gain +3 to the Difficulty of a reaction roll the spell causes.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "What did you do that made the people in your community wary of you?",
      "What mentor taught you to control your untamed magic, and why are they no longer able to guide you?",
      "You have a deep fear you hide from everyone. What is it, and why does it scare you?",
    ],
    connectionQuestions: [
      "Why do you trust me so deeply?",
      "What did I do that makes you cautious around me?",
      "Why do we keep our shared past a secret?",
    ],
  },
  {
    name: 'Warrior',
    tagline: 'As a warrior, you run into battle without hesitation or caution, knowing you can strike down whatever enemy stands in your path.',
    description: [
      "Becoming a warrior requires years, often a lifetime, of training and dedication to the mastery of weapons and violence. While many who seek to fight hone only their strength, warriors understand the importance of an agile body and mind, making them some of the most sought-after fighters across the realms.",
      "Frequently, warriors find employment within an army, a band of mercenaries, or even a royal guard, but their potential is wasted in any position where they cannot continue to improve and expand their skills. Warriors are known to have a favored weapon; to come between them and their blade would be a grievous mistake.",
    ],
    stats: {
      domains: 'Blade & Bone',
      evasion: 11,
      hp: 6,
      suggestedTraits: '+2, +1, 0, +1, -1, 0',
      suggestedPrimary: 'Longsword',
      suggestedSecondary: null as unknown as string,
      suggestedArmor: 'Chainmail Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['the drawing of a lover', 'a sharpening stone'],
    },
    characterDescription: {
      clothes: ['bold', 'patched', 'reinforced', 'royal', 'sleek', 'sparing', 'weathered'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a bull', 'a dedicated soldier', 'a gladiator', 'a hero', 'a hired hand'],
    },
    hopeFeature: 'No Mercy: Spend 3 Hope to gain a +1 bonus to your attack rolls until your next rest.',
    classFeatures: [
      {
        name: 'Attack of Opportunity',
        description: "If an adversary within Melee range attempts to leave that range, make a reaction roll using a trait of your choice against their Difficulty. On a success, choose one effect; on a critical success, choose two: they can't move from where they are; you deal damage to them equal to your primary weapon's damage; or you move with them.",
      },
      {
        name: 'Combat Training',
        description: 'You ignore burden when equipping weapons. When you deal physical damage, you gain a bonus to your damage roll equal to your level.',
      },
    ],
    subclasses: {
      'Call of the Brave': {
        name: 'Call of the Brave',
        foundation: [
          {
            name: 'Courage',
            description: 'When you fail a roll with Fear, you gain a Hope.',
          },
          {
            name: 'Battle Ritual',
            description: 'Once per long rest, before something incredibly dangerous or when facing a clearly superior foe, describe your ritual or mental preparations. Clear 2 Stress and gain 2 Hope.',
          },
        ],
        specialization: [
          {
            name: 'Rise to the Challenge',
            description: 'While you have 2 or fewer Hit Points remaining and unmarked, you can roll a d20 as your Hope Die.',
          },
        ],
        mastery: [
          {
            name: 'Camaraderie',
            description: 'You can initiate a Tag Team Roll one additional time per session. When an ally initiates a Tag Team Roll with you, they only need to spend 2 Hope instead of 3.',
          },
        ],
      },
      'Call of the Slayer': {
        name: 'Call of the Slayer',
        foundation: [
          {
            name: 'Slayer',
            description: "Gain a pool of Slayer Dice. On any roll with Hope, place a d6 in your Slayer pool instead of gaining Hope. You can store up to your Proficiency in Slayer Dice. When making an attack or damage roll, spend any number of Slayer Dice to roll and add them. At the end of each session, clear all unspent Slayer Dice and gain 1 Hope per die cleared.",
          },
        ],
        specialization: [
          {
            name: 'Weapon Specialist',
            description: "Spend a Hope after a successful attack to add one damage die from your secondary weapon to the damage roll. Once per long rest when rolling Slayer Dice, reroll any dice that show a 1.",
          },
        ],
        mastery: [
          {
            name: 'Martial Preparation',
            description: 'Your party gains access to the Martial Preparation downtime move. Describe instructing and training with your party; you and each participating ally gain a d6 Slayer Die that can be used on any attack or damage roll.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "Who taught you to fight, and why did they stay behind when you left home?",
      "Somebody defeated you in battle years ago and left you to die. Who was it, and how did they betray you?",
      "What legendary place have you always wanted to visit, and why is it so special?",
    ],
    connectionQuestions: [
      "We knew each other long before this party came together. How?",
      "What mundane task do you usually help me with off the battlefield?",
      "What fear am I helping you overcome?",
    ],
  },
  {
    name: 'Wizard',
    tagline: "As a wizard, you've become familiar with the arcane through the relentless study of grimoires and other tools of magic.",
    description: [
      "Whether through an institution or individual study, those known as wizards acquire and hone immense magical power over years of learning using a variety of tools, including books, stones, potions, and herbs. Some wizards dedicate their lives to mastering a particular school of magic, while others learn from a wide variety of disciplines.",
      "Many wizards become wise and powerful figures in their communities, advising rulers, providing medicines and healing, and even leading war councils. While these mages all work toward the common goal of collecting magical knowledge, wizards often have the most conflict within their own ranks, as the acquisition, keeping, and sharing of powerful secrets is a topic of intense debate that has resulted in innumerable deaths.",
    ],
    stats: {
      domains: 'Codex & Splendor',
      evasion: 11,
      hp: 5,
      suggestedTraits: '-1, 0, 0, +1, +1, +2',
      suggestedPrimary: 'Greatstaff',
      suggestedSecondary: null as unknown as string,
      suggestedArmor: 'Leather Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ["a book you're trying to translate", 'a tiny, harmless elemental pet'],
      thenDecideWhatYouUseForYourCraft: ['large tomes', 'tarot cards', 'etc.'],
    },
    characterDescription: {
      clothes: ['beautiful', 'clean', 'common', 'flowing', 'layered', 'patchwork', 'tight'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['an eccentric', 'a librarian', 'a lit fuse', 'a philosopher', 'a professor'],
    },
    hopeFeature: "Not This Time: Spend 3 Hope to force an adversary within Far range to reroll an attack or damage roll.",
    classFeatures: [
      {
        name: 'Prestidigitation',
        description: "You can perform harmless, subtle magical effects at will. For example, you can change an object's color, create a smell, light a candle, cause a tiny object to float, illuminate a room, or repair a small object.",
      },
      {
        name: 'Strange Patterns',
        description: 'Choose a number between 1 and 12. When you roll that number on a Duality Die, gain a Hope or clear a Stress. You can change this number when you take a long rest.',
      },
    ],
    subclasses: {
      'School of Knowledge': {
        name: 'School of Knowledge',
        spellcastTrait: 'Knowledge',
        foundation: [
          {
            name: 'Prepared',
            description: 'Take an additional domain card of your level or lower from a domain you have access to.',
          },
          {
            name: 'Adept',
            description: 'When you Utilize an Experience, mark a Stress instead of spending a Hope to double your Experience modifier for that roll.',
          },
        ],
        specialization: [
          {
            name: 'Accomplished',
            description: 'Take another additional domain card of your level or lower from a domain you have access to.',
          },
          {
            name: 'Perfect Recall',
            description: 'Once per rest, when you recall a domain card from your vault, reduce its Recall Cost by 1.',
          },
        ],
        mastery: [
          {
            name: 'Brilliant',
            description: 'Take yet another additional domain card of your level or lower from a domain you have access to.',
          },
          {
            name: 'Honed Expertise',
            description: "When you use an Experience, roll a d6. On a result of 5 or higher, you can use the Experience without spending a Hope.",
          },
        ],
      },
      'School of War': {
        name: 'School of War',
        spellcastTrait: 'Knowledge',
        foundation: [
          {
            name: 'Battlemage',
            description: 'You gain an additional Hit Point slot.',
          },
          {
            name: 'Face Your Fear',
            description: 'When you succeed with Fear on an attack roll, deal an extra 1d10 magic damage.',
          },
        ],
        specialization: [
          {
            name: 'Conjure Shield',
            description: 'While you have at least 2 Hope, add your Proficiency to your Evasion.',
          },
          {
            name: 'Fueled by Fear',
            description: 'The extra damage from Face Your Fear increases to 2d10.',
          },
        ],
        mastery: [
          {
            name: 'Thrive in Chaos',
            description: 'When you succeed on an attack, mark a Stress after rolling damage to force the target to mark an additional Hit Point.',
          },
          {
            name: 'Have No Fear',
            description: 'The extra damage from Face Your Fear increases to 3d10.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "What responsibilities did your community once count on you for? How did you let them down?",
      "You've spent your life searching for a book or object of great significance. What is it, and why is it so important to you?",
      "You have a powerful rival. Who are they, and why are you so determined to defeat them?",
    ],
    connectionQuestions: [
      "What favor have I asked of you that you're not sure you can fulfill?",
      "What weird hobby or strange fascination do we both share?",
      "What secret about yourself have you entrusted only to me?",
    ],
  },
  {
    name: 'Warlock',
    source: 'Void',
    description: [
      "As a warlock, you've pledged your life to a patron in exchange for great power. Warlocks bind themselves to a patron — a god, demon, fae, or other supernatural entity — and draw on that relationship to cast spells, manipulate others, and endure what others cannot.",
      "Unlike those who study magic or wield it by birthright, a warlock's power is borrowed. The debt is real. Warlocks must constantly maintain their patron's favor through offerings and obedience, or risk losing everything they have bargained for.",
    ],
    stats: {
      domains: 'Dread & Grace',
      evasion: 11,
      hp: 5,
      suggestedTraits: '+1, -1, 0, +1, +2, 0',
      suggestedPrimary: 'Scepter',
      suggestedArmor: 'Leather Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a carving that symbolizes your patron', "a ring you can't remove"],
    },
    characterDescription: {
      clothes: ['shadowy', 'billowing', 'smoking', 'lavish', 'sacred', 'mended', 'neat', 'luminous'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a coming storm', 'a devotee', 'a jester', 'a soothsayer', 'a monarch', 'a live wire', 'a hot mess'],
    },
    hopeFeature: "Patron's Boon: Spend 3 Hope to call out to your patron for help, gaining 1d4 Favor.",
    classFeatures: [
      {
        name: 'Warlock Patron',
        description: "You have committed yourself to a patron (god, demon, fae, or other supernatural entity) in exchange for power. Write their name on your character sheet. Then choose their spheres of influence, at GM discretion (Nature & Mischief, Love & War, Knowledge & Shadow, etc.), record them below, and set their values to +2. Anytime you increase your tier, these spheres of influence gain a permanent +1 bonus. Before making an action roll that relates to one of your patron's spheres of influence, you can spend a Favor to call on them and add its value to the roll.",
      },
      {
        name: 'Favor',
        description: "Start with 3 Favor. During a rest, spend one of your downtime moves to tithe to your patron. When you do, gain Favor equal to your Presence. If you choose to forgo this offering, the GM instead gains a Fear.",
      },
    ],
    subclasses: {
      'Pact of the Endless': {
        name: 'Pact of the Endless',
        spellcastTrait: 'Presence',
        foundation: [
          {
            name: "Patron's Mantle",
            description: "Mark a Stress to cloak yourself in a terrifying aspect of your Patron that lasts until you take Severe damage or the scene ends. While active: When you would mark an Armor Slot, you can spend 2 Favor instead. You gain a bonus equal to your tier on action rolls to intimidate a target.",
          },
          {
            name: 'Deadly Devotion',
            description: 'On a successful attack, you can spend a Favor to gain a +1 bonus to your Evasion until you mark a Hit Point or take a rest.',
          },
          {
            name: 'Herald of the Deal',
            description: 'When you fail an attack roll, you can spend a Favor to reroll it. If it fails again, mark a Stress and take the new result.',
          },
        ],
        specialization: [
          {
            name: 'Draining Invocation',
            description: 'When an adversary attacks you or an ally within Very Close range, you can spend a Favor to make them use a d12 instead of a d20 for the attack roll. Additionally, the adversary must mark a Stress, and you can clear a Stress.',
          },
        ],
        mastery: [
          {
            name: 'Dark Aegis',
            description: 'Once per long rest, when you would mark any number of Hit Points, you can spend a Favor to temporarily Drain them. When you do, they must mark a Stress and you can clear a Stress. A drained creature uses a d12 instead of a d20 for attack rolls.',
          },
          {
            name: 'Draining Bane',
            description: 'When an adversary attacks you or an ally within Very Close range, you can spend 2 Favor to temporarily Drain them. When you do, they must mark a Stress and you can clear a Stress. A drained creature uses a d12 instead of a d20 for attack rolls.',
          },
        ],
      },
      'Pact of the Wrathful': {
        name: 'Pact of the Wrathful',
        spellcastTrait: 'Presence',
        foundation: [
          {
            name: 'Favored Weapon',
            description: "Mark a Stress to Imbue your weapon with your Patron's fury until you deal Severe damage. On a successful Imbued weapon attack, you can spend any number of Favor to gain a +1d6 damage bonus for each Favor spent.",
          },
          {
            name: 'Herald of the Deal',
            description: 'When you fail an attack roll, you can spend a Favor to reroll it. If it fails again, mark a Stress and take the new result.',
          },
        ],
        specialization: [
          {
            name: 'Menacing Reach',
            description: "When you Imbue your weapon with your Patron's fury, you can mark an additional Stress to increase its range by one step (Melee to Very Close, Very Close to Close, etc.).",
          },
          {
            name: 'Diminish My Foes',
            description: 'When you succeed with Hope on an action roll against a target, you can spend a Hope to make your target mark a Stress.',
          },
        ],
        mastery: [
          {
            name: 'Fearsome Attack',
            description: 'You can always spend a Favor to reroll any number of your damage dice. You can continue spending Favor to reroll the same dice as many times as you\'d like.',
          },
          {
            name: 'Divine Ire',
            description: 'Once per rest, when you take damage, you can spend any number of Favor to target that many adversaries within Close range. Each target must mark a Hit Point.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "What desperate situation led you to pledge your life to your patron?",
      "Your patron has given you one task you must accomplish above all else. What is it, and why does it worry you?",
      "You and your patron are similar in a very specific way. What characteristic do you share, and how do you feel about it?",
    ],
    connectionQuestions: [
      "I confide in you about what my patron says and does. Why?",
      "You once saw me tithe to my patron and it's changed how you interact with me. What did you see and how has it affected you?",
      "I once did something very foolish, and you have never let me live it down. What was it?",
    ],
  },
  {
    name: 'Blood Hunter',
    source: 'Void',
    description: [
      "Blood hunters are warriors who have unlocked a dangerous, forbidden power: the ability to channel magic through their own blood. They use hemocraft to enchant their weapons, read the history of violence in objects and places, and hunt the monsters that plague the Mortal Realms.",
      "This power comes at a cost. Every enchantment, every ritual, every vision is paid for in vitality. Blood hunters walk a fine line between strength and self-destruction, driven by a duty to protect others from threats they alone are equipped to face.",
    ],
    stats: {
      domains: 'Blade & Blood',
      evasion: 9,
      hp: 7,
      suggestedTraits: '+2, -1, +1, +1, 0, 0',
      suggestedPrimary: ['Longsword', 'Battleaxe'],
      suggestedArmor: 'Leather Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a steel needle', "a vial holding a foe's blood"],
    },
    characterDescription: {
      clothes: ['bloodstained', 'crimson', 'distressed', 'patched', 'rune-patterned', 'snug', 'spiky'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a stern mentor', 'a dashing vampire', 'a starving wolf', 'a surgeon', 'a thundercloud', 'a trapdoor spider'],
    },
    hopeFeature: 'Blood Maledict: Spend 3 Hope to target a creature within Far range or in a vision from your Grim Psychometry. Until you finish a rest, take Severe damage, or use this feature again, you have advantage on all action rolls against the target.',
    classFeatures: [
      {
        name: 'Crimson Rite',
        description: 'You can enchant your strikes with bloodthirsty power at the cost of your vitality. Mark a Hit Point to enchant one of your active weapons. Until you finish your next rest, that weapon deals physical or magic damage (choose when you use this feature) and an extra 1d6 damage when you hit with it. This extra damage increases to 2d6 at level 5 and 3d6 at level 8.',
      },
      {
        name: 'Grim Psychometry',
        description: 'While inspecting a creature, a location, or an object within Very Close range, make a Spellcast Roll (12). On a success, mark a Stress to have a vision of the most recent violence involving the target, and until you finish a rest, you have advantage on any action roll to recall lore about things in the vision.',
      },
    ],
    subclasses: {
      'Order of the Ghost Slayer': {
        name: 'Order of the Ghost Slayer',
        spellcastTrait: 'Agility',
        foundation: [
          {
            name: 'Chasing Death',
            description: 'While you have 1-3 unmarked Hit Points, you can use your Crimson Rite feature without paying the Hit Point cost. If you have only 1 unmarked Hit Point, you roll d8s instead of d6s for the extra damage of your Crimson Rite.',
          },
          {
            name: 'Shadowed Grit',
            description: 'When the GM gains a Fear from a Duality Dice roll, you can mark a Stress to gain a Hope.',
          },
        ],
        specialization: [
          {
            name: 'Veilwalker',
            description: 'You can briefly slip into the realm between the living and the dead. Spend 2 Hope to disappear and then reappear next to a creature or a corpse within Far range. You then have advantage on the next attack roll you make in this scene.',
          },
        ],
        mastery: [
          {
            name: 'Spectral Form',
            description: "You have another death move option: Spectral Form. When you choose this option, you clear a Hit Point, your body becomes spectral, you have resistance to physical damage, and you can pass through objects. This form ends if you clear any more Hit Points. It also ends if you mark your last Hit Point, which forces you to take a different death move. You're shunted to the nearest open space if you're inside an object when the form ends.",
          },
        ],
      },
      'Order of the Mutant': {
        name: 'Order of the Mutant',
        spellcastTrait: 'Agility',
        foundation: [
          {
            name: 'Mutagens',
            description: 'You employ mutagenic toxins created to enhance your abilities. When you finish a rest, you can drink one such toxin to gain its effects, which last until you finish your next rest. These effects include a +1 bonus to one trait of your choice, a -1 penalty to a trait of your choice, and your choice of one of the following benefits. Celerity: If you are Restrained or Vulnerable, you can mark a Stress to end the condition on yourself. Durable: Your Armor Score increases by 2. Hunter\'s Senses: You have advantage on any action roll you make to track a creature, and you can see in complete darkness.',
          },
        ],
        specialization: [
          {
            name: 'Improved Mutagens',
            description: "When you choose a mutagen benefit, your options also include the following. Necrotic Skin of Steel: Whenever you mark a Stress, you can spend 2 Hope instead. Rapidity: Your Evasion increases by 1. Ironskin: Your Severe damage threshold increases by a number equal to your Proficiency. Volatile Toxins: You can choose two mutagen benefits instead of one. If you do, mark a Hit Point that can't be cleared until you finish your next rest.",
          },
        ],
        mastery: [
          {
            name: 'Mastered Mutagens',
            description: 'The trait bonus and penalty you get from your Mutagens feature changes to +2 and -2, respectively. When you choose a mutagen benefit, your options also include the following. Aetherblood: Within your line of sight, you can see creatures and objects that are invisible, and magical illusions appear transparent to you. If a creature or an object emits light to appear or is being transformed by magic, you can see its true form. Fury: When you make an attack roll, you can mark a Stress to gain a bonus to the roll equal to your Proficiency. Steelflesh: Your Major damage threshold increases by a number equal to your Proficiency.',
          },
        ],
      },
      'Order of the Lycan': {
        name: 'Order of the Lycan',
        spellcastTrait: 'Strength',
        foundation: [
          {
            name: 'Werewolf',
            description: 'You gain the Werewolf Transformation card. Your rolls to perceive via hearing and scent have advantage.',
          },
          {
            name: 'Control the Beast',
            description: 'While in your Wolf Form, you can apply that form\'s bonus die to your Agility and Strength Action Rolls.',
          },
        ],
        specialization: [
          {
            name: 'Feral Rite',
            description: 'While in your Wolf Form, you gain a bonus to your Damage Thresholds equal to your Tier.',
          },
          {
            name: 'Lycan Regeneration',
            description: 'While in your Wolf Form with all your Hit Points marked, you can mark a Stress to clear a Hit Point. You can do this while unconscious.',
          },
        ],
        mastery: [
          {
            name: 'Master the Beast',
            description: "When you are in your Wolf Form and would mark your last Stress, you can instead put one of your Domain cards from your Loadout into your Vault; you can't Recall that Domain card until you finish a long rest. When you Frenzy from marking your last Stress while in Wolf Form, you gain 2 Hope.",
          },
        ],
      },
    },
    backgroundQuestions: [
      "Are you descended from blood hunters, or are you the first in your bloodline to practice hemocraft?",
      "Which blood relative taught you an important lesson, and how has that lesson kept you alive?",
      "Who or what was your first kill?",
    ],
    connectionQuestions: [
      "What have you heard about blood magic that makes you afraid of me or concerned for my well-being?",
      "What act of bloodshed brought us together?",
      "How does my determination to rid the Mortal Realms of evildoers align with your beliefs?",
    ],
  },
  {
    name: 'Assassin',
    source: 'Void',
    description: [
      "Assassins are precise, patient killers who have turned the art of death into a discipline. Whether trained by a shadowy guild, raised in a culture that honored the craft, or self-taught through necessity, they bring ruthless efficiency to every engagement.",
      "What sets an assassin apart is not just the kill, but the control. They choose when to strike, how to vanish, and who gets to walk away.",
    ],
    stats: {
      domains: 'Blade & Midnight',
      evasion: 12,
      hp: 5,
      suggestedTraits: '+2, -1, +1, +0, +0, +1',
      suggestedPrimary: 'Broadsword',
      suggestedSecondary: 'Short Sword',
      suggestedArmor: 'Leather Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a list of names with several marked off', 'a mortar and pestle inscribed with a mysterious insignia'],
    },
    characterDescription: {
      clothes: ['sinister', 'weathered', 'hooded', 'finely tailored', 'incognito', 'padded for silence'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a hidden razor', 'a judge', 'an owl', 'a butcher', 'a coiled viper', 'a merchant', 'a hunter'],
    },
    hopeFeature: 'Grim Resolve: Spend 3 Hope to clear 2 Stress.',
    classFeatures: [
      {
        name: 'Marked for Death',
        description: "On a successful weapon attack, you can mark a Stress to make the target Marked for Death. Attacks you make against a target that's Marked for Death gain a bonus to damage equal to +1d4 per tier. You can only have one adversary Marked for Death at a time, and can't transfer or remove the condition except by defeating the target. The GM can spend a number of Fear equal to your Proficiency to remove the Marked for Death condition. Otherwise, it ends automatically when you take a rest.",
      },
      {
        name: 'Get In & Get Out',
        description: 'Spend a Hope to ask the GM for either a quick or inconspicuous way into or out of a building or structure you can see. The next roll you make that capitalizes on this information has advantage.',
      },
    ],
    subclasses: {
      'Executioners Guild': {
        name: 'Executioners Guild',
        spellcastTrait: 'Agility',
        foundation: [
          {
            name: 'First Strike',
            description: 'The first time in a scene you succeed on an attack roll, add the damage of the attack.',
          },
          {
            name: 'Ambush',
            description: 'Your "Marked for Death" feature uses d6s instead of d4s.',
          },
        ],
        specialization: [
          {
            name: 'Death Strike',
            description: 'When you deal Severe damage to a creature, you can mark a Stress to make them mark an additional Hit Point.',
          },
          {
            name: "Scorpion's Poise",
            description: 'You gain a +2 bonus to your Evasion against any attacks made by a creature Marked for Death.',
          },
        ],
        mastery: [
          {
            name: 'True Strike',
            description: 'Once per long rest, when you fail an attack roll, you can spend a Hope to make it a success instead.',
          },
          {
            name: 'Backstab',
            description: 'Your "Marked for Death" feature uses d8s instead of d4s.',
          },
        ],
      },
      'Poisoners Guild': {
        name: 'Poisoners Guild',
        spellcastTrait: 'Knowledge',
        foundation: [
          {
            name: 'Toxic Concoctions',
            description: 'Mark a Stress to add 1d4+1 tokens to this card. On your next long rest, clear this card. You know these poisons: Gorgon Root: The target gains a permanent -1 penalty to their Difficulty. This can only affect them once. Grave Spore: The target must also mark a Stress. Leech Weed: Gain a 1d6 damage bonus on this attack.',
          },
          {
            name: 'Envenomate',
            description: "When you make a successful weapon attack, you can spend a token from this card to afflict the target with a known poison's effect.",
          },
        ],
        specialization: [
          {
            name: 'Poison Compounds',
            description: "You also know these poisons: Midnight's Veil: The target gains a permanent -2 penalty to attack rolls. This can only affect them once. Ghost Petal: Permanently decrease the damage dice of the target's standard attack by one step (d10 to d8, d8 to d6, etc.). This can only affect them once. Adder's Blessing: You are immune to poisons and other toxins.",
          },
        ],
        mastery: [
          {
            name: 'Venomancer',
            description: 'You also know these poisons: Blight Seed: The target gains a permanent -3 penalty to their damage thresholds. This can only affect them once. Fear Leaf: This attack gains a damage bonus equal to the result of your Fear Die. Twin Fang: When you afflict a target with a known poison\'s effect, you can spend an additional token to inflict the effect of a second known poison.',
          },
        ],
      },
    },
    backgroundQuestions: [
      "What organization trained you in the art of killing, and how did you gain membership into it?",
      "Throughout your entire career, one target has eluded you. Who are they, and how have they managed to slip through your fingers?",
      "You always do what you must to take down your target, but there's one line that you will never cross. What is it?",
    ],
    connectionQuestions: [
      "What about me frightens you?",
      "You once asked me to do something that keeps you up at night. What was it?",
      "What secret about myself did I tell you, and how did it change your view of me?",
    ],
  },
  {
    name: 'Brawler',
    source: 'Void',
    description: [
      "Brawlers are fighters who trust their bodies above all else. Whether through disciplined martial practice or raw instinct, they have mastered the art of close-range combat, turning every limb into a weapon and every blow into an opportunity.",
      "What makes a brawler dangerous is their adaptability. They read a fight as it unfolds, shifting stances, exploiting openings, and wearing down opponents through relentless pressure.",
    ],
    stats: {
      domains: 'Bone & Valor',
      evasion: 10,
      hp: 6,
      suggestedTraits: '+1, +1, 0, +2, 0, -1',
      suggestedPrimary: 'Quarterstaff',
      suggestedArmor: 'Leather Armor',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['hand wraps from a mentor', 'a book about your secret hobby'],
    },
    characterDescription: {
      clothes: ['practical', 'haphazard', 'pristine', 'standard-issue', 'bright', "someone else's"],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a loose cannon', 'a parent', 'a veteran', 'a flowing river', 'a golden retriever', 'a protector'],
    },
    hopeFeature: 'Staggering Strike: Spend 3 Hope when you succeed on an attack to temporarily Stagger your target and force them to mark a Stress. While Staggered, they have disadvantage on attack rolls.',
    classFeatures: [
      {
        name: 'I Am the Weapon',
        description: "While you don't have any equipped weapons: You gain a +1 bonus to Evasion. Your unarmed strikes are considered a Melee weapon, use the trait of your choice, and deal d8+d6 phy damage using your Proficiency.",
      },
      {
        name: 'Combo Strikes',
        description: 'After making a damage roll with a Melee weapon but before dealing that damage to the target, mark a Stress to start a combo strike. When you do, roll your Combo Die and note its value. Then, roll your Combo Die again. If the value of the second roll is equal to or greater than your first roll, continue rolling until the latest Combo Die\'s roll is less than the roll that preceded it. Total all rolled values and add that amount to your weapon\'s damage. These values cannot be adjusted by features that affect damage dice. Your Combo Die starts as a d4. When you level up, once per tier you may use one of your advancement options to increase your Combo Die instead.',
      },
    ],
    subclasses: {
      Juggernaut: {
        name: 'Juggernaut',
        spellcastTrait: 'Strength',
        foundation: [
          {
            name: 'Powerhouse',
            description: 'Increase the d8 damage dice for your unarmed attack to d10s. Additionally, you can mark a Stress to target two creatures within Melee range with a single attack.',
          },
          {
            name: 'Overwhelm',
            description: 'On a successful attack, you can spend a Hope to force the target to mark a Stress or to throw them within Close range.',
          },
        ],
        specialization: [
          {
            name: 'Rugged',
            description: 'Gain a permanent +3 bonus to your Severe damage threshold. Additionally, your Powerhouse feature can target up to three creatures instead of two.',
          },
          {
            name: 'Eye for an Eye',
            description: 'When you lose more than one Hit Point from an attack in Melee range, the attacker must make a Reaction Roll (13). On a failure, once per rest, they immediately mark the same number of Hit Points in return.',
          },
        ],
        mastery: [
          {
            name: 'Pummeljoy',
            description: 'When you critically succeed on a weapon attack in Melee range, you gain an additional Hope, clear an additional Stress, and gain a +1 bonus to your Proficiency for the attack.',
          },
          {
            name: 'Not Done Yet',
            description: 'When you mark more than one HP from an attack, you can gain a Hope or clear a Stress.',
          },
        ],
      },
      'Martial Artist': {
        name: 'Martial Artist',
        spellcastTrait: 'Instinct',
        foundation: [
          {
            name: 'Martial Form',
            description: 'Take the Martial Form sheet. You start with two martial stances from Tier 1. When you reach a new tier, take two additional stances at your tier or lower.',
          },
          {
            name: 'Focus',
            description: 'During a rest, roll a number of d6s equal to your Instinct and place a number of Focus tokens equal to the lowest value rolled on this card. Spend a Focus to shift into a stance until you take Severe damage, the scene ends, you mark your last Hit Point, or you shift into another stance.',
          },
        ],
        specialization: [
          {
            name: 'Keen Defense',
            description: 'When you are targeted by an attack, you can spend a Focus to give the attack roll disadvantage.',
          },
          {
            name: 'Spirit Blast',
            description: 'Spend a Focus to make an Instinct roll against an adversary within Close range. On a success, deal d20+3 magic damage using your Proficiency, and you can spend an additional Focus to make them temporarily Vulnerable.',
          },
        ],
        mastery: [
          {
            name: 'Limit Breaker',
            description: 'Once per rest, you can perform an unbelievable feat such as running across water, leaping between distant rooftops, or scaling the side of a building without needing to roll. When you do, gain a Hope and clear a Stress.',
          },
        ],
        martialForm: {
          description: 'Spend a Focus to activate or shift into a different stance. It lasts until you mark your last Hit Point, you take Severe damage, the scene ends, or you shift into another martial stance.',
          tiers: {
            '1': [
              { name: 'Brutal', description: 'When you roll the maximum value on a damage die, roll an additional damage die.' },
              { name: 'Defensive', description: 'Attack rolls against you have disadvantage unless the adversary marks a Stress.' },
              { name: 'Grappling', description: 'On a successful attack, you can spend a Focus to make the target temporarily Restrained.' },
              { name: 'Steady', description: 'Gain a -1 penalty to your Evasion. When you deal damage on a successful attack, roll an additional damage die and drop the lowest.' },
              { name: 'Precise', description: 'Gain a +1 bonus to your attack rolls.' },
              { name: 'Quick', description: 'When making an attack roll, spend a Focus to include an additional target within range.' },
            ],
            '2': [
              { name: 'Deadly', description: 'When you deal Severe damage, the target marks an additional HP.' },
              { name: 'Hindering', description: 'On a successful attack, you can spend a Focus to make the target temporarily Hindered. While Hindered, their attack rolls have a -2 penalty.' },
              { name: 'Invigorating', description: 'When you make a successful attack, roll a d4. On a 4, gain a Focus.' },
              { name: 'Immovable', description: 'Gain a +2 bonus to your damage thresholds and you cannot be moved unwillingly.' },
              { name: 'Nimble', description: 'When an adversary makes a successful attack roll against you, you can spend a number of Focus up to your Tier. Roll a number of d6s equal to 1 + the Focus spent. If any of the dice values match, you take no damage from the attack.' },
              { name: 'Otherworldly', description: 'You can choose whether to do Physical or Magic damage.' },
              { name: 'Scary', description: 'Successful attacks also force the target to mark a Stress.' },
            ],
            '3': [
              { name: 'Deflecting', description: 'When you are targeted by an attack, you can spend 2 Focus to gain a bonus to your Evasion equal to your Armor Score against the attack.' },
              { name: 'Devastating', description: 'Spend a Focus before your attack roll to use d20s as your damage dice instead.' },
              { name: 'Dueling', description: 'Gain advantage on attack rolls when there are no other adversaries or allies in Very Close range of you or your target.' },
              { name: 'Compounding', description: 'When you roll the maximum value on a Combo Die, increase the size of the subsequent Combo Dice you roll for the attack (at a maximum of d12s).' },
              { name: 'Inexorable', description: 'Attacks against you have disadvantage if you have more than two adversaries within Melee of you.' },
              { name: 'Favored', description: 'Add the trait of your choice to your damage roll.' },
              { name: 'Sheltering', description: 'When you mark an Armor Slot, it reduces damage for you and all allies within Melee range of you who took the same damage.' },
            ],
            '4': [
              { name: 'Crushing', description: 'When you roll a 1 on a damage die, it can be treated as the highest value on the die instead.' },
              { name: 'Infuriating', description: 'On a successful attack, the target becomes Furious. While Furious, they are Vulnerable and have a -2 penalty to their attack rolls. They can only clear Furious by succeeding on an attack against you.' },
              { name: 'Sweeping', description: 'Your attack targets all adversaries within Very Close range. Deal half damage to all targets you succeed against.' },
              { name: 'Discerning', description: 'You may choose the target of your attack after making your attack roll. The GM will tell you who the attack would succeed against.' },
            ],
          },
        },
      },
    },
    backgroundQuestions: [
      "Where did you spend time during your formative years that taught you, directly or indirectly, how to fight in the style you use?",
      "What group or organization has always had your back, and how did you get in their good graces?",
      "Who did you lose a fight to long ago that you are desperate for a rematch against?",
    ],
    connectionQuestions: [
      "What is one thing we're both afraid of?",
      "I rely on you for something important during our travels together. What is it and how do you feel about it?",
      "I still haven't forgiven you for something you said to me. What was it and why did you say it?",
    ],
  },
  {
    name: 'Witch',
    source: 'Void',
    description: [
      "Witches are practitioners of ancient, intuitive magic that flows from deep bonds with spirits, nature, and the unseen world. They bend fate, curse enemies, and commune with forces beyond mortal understanding.",
      "Witches are often enigmatic, patient, and perceptive, possessing an uncanny ability to read people and situations. Whether casting hexes or brewing remedies, a Witch's power comes from attunement — to the world, to others, and to the liminal spaces between life and death.",
    ],
    stats: {
      domains: 'Dread, Sage',
      evasion: 10,
      hp: 6,
      suggestedTraits: '0, -1, 0, +2, +1, +1',
      suggestedPrimary: 'Dualstaff',
      suggestedArmor: 'Gambeson',
    },
    inventory: {
      take: 'a torch, 50 feet of rope, basic supplies, and a handful of gold',
      chooseBetween: ['a Minor Health Potion', 'a Minor Stamina Potion'],
      andEither: ['a small, harmless pet', 'a talking skull'],
      thenDecideWhatYouUseForYourCraft: ['handwritten journal', 'runestones', 'etc.'],
    },
    characterDescription: {
      clothes: ['foreboding', 'ragged', 'flowing', 'stately', 'diaphanous', 'uniquely patterned'],
      eyes: ['carnations', 'earth', 'endless ocean', 'fire', 'ivy', 'lilacs', 'night', 'seafoam', 'winter'],
      body: ['broad', 'carved', 'curvy', 'lanky', 'rotund', 'short', 'stocky', 'tall', 'thin', 'tiny', 'toned'],
      skin: ['ashes', 'clover', 'falling snow', 'fine sand', 'obsidian', 'rose', 'sapphire', 'wisteria'],
      attitude: ['a spider', 'a sunny day', 'a doctor', 'a candle flame', 'an old tree', 'a cat', 'an oracle'],
    },
    hopeFeature: "Witch's Charm: When you or an ally within Far range rolls a failure on an action roll, you can spend 3 Hope to change it into a success with Fear instead.",
    classFeatures: [
      {
        name: 'Hex',
        description: "When a creature causes you or an ally within Close range to mark any number of Hit Points, you can mark a Stress to Hex them. Action and damage rolls against a Hexed creature gain a bonus equal to your tier. This condition lasts until the GM spends a number of Fear equal to your Spellcast trait to remove it or you Hex another creature. Otherwise, remove it when the scene ends.",
      },
      {
        name: 'Commune',
        description: "Once per long rest, during a moment of calm, you can commune with an ancestor, deity, nature spirit, or otherworldly being. Ask them a question, then roll a number of d6s equal to your Spellcast trait. Choose one value from the rolled results:\n1-3: You taste a flavor, smell a scent, or feel a sensation relevant to the answer.\n4-5: You hear sounds or see a vision relevant to the answer.\n6: You psychically experience a scene relevant to the answer as if you were there.",
      },
    ],
    subclasses: {
      Hedge: {
        name: 'Hedge',
        spellcastTrait: 'Knowledge',
        foundation: [
          { name: 'Herbal Remedies', description: 'When you or an ally clear one or more Hit Points or Stress as the result of using a consumable, increase the number cleared by one.' },
          { name: 'Tethered Talisman', description: "Once per rest, you can imbue a small item with your protective essence. When the person holding the talisman takes damage, you can expend its magic to reduce the number of Hit Points they mark by one. You can't create a new talisman until the old one has been used." },
        ],
        specialization: [
          { name: 'Walk Between Worlds', description: 'Make a Spellcast Roll (13) to step beyond the veil of death and converse with any nearby spirits. Place a number of tokens equal to your Spellcast trait on this card and remove one each time a spirit answers a question. You return to the mortal realm when the last token is removed.' },
          { name: 'Enhanced Hex', description: 'Attacks you make against Hexed creatures gain a damage bonus equal to your Proficiency.' },
        ],
        mastery: [
          { name: 'Circle of Power', description: 'Once per rest, mark a circle on the ground around you up to Very Close range and place a number of tokens equal to your Spellcast Trait on this card. Each time you or any ally within the circle makes an action roll or is hit with an attack, remove a token. This spell lasts until the last token is removed or you step out of the circle. While within this circle, you and any allies: Gain a +4 bonus to your damage thresholds. Gain a +2 bonus to your attack rolls. Gain a +1 bonus to your Evasion.' },
        ],
      },
      Moon: {
        name: 'Moon',
        spellcastTrait: 'Instinct',
        foundation: [
          { name: "Night's Glamour", description: "Mark a Stress to Glamour yourself in a magical facade that lasts until you mark a Hit Point, make an attack, or take a rest. While Glamoured, you can: Disguise yourself to look like any creature of your approximate size that you've seen. Enhance your own appearance. You gain advantage on Presence Rolls that leverage this change." },
        ],
        specialization: [
          { name: 'Moonbeam', description: 'Once per session, you can conjure a column of moonlight that illuminates the area within Close range until the end of the scene. While bathed in this moonlight, you and any allies gain a +1 bonus to Spellcast Rolls and advantage on rolls to see through illusions.' },
          { name: 'Ire of Pale Light', description: 'When a Hexed creature within Far range fails an attack roll, they must mark a Stress.' },
        ],
        mastery: [
          { name: 'Lunar Phases', description: 'Your spirit ebbs and flows like the phases of the moon. At the beginning of each session, roll a d4 and gain the matching effect until the end of the session: 1 (New): You can always spend a Hope to reduce Minor damage to None. 2 (Waxing): Gain a +2 bonus to your damage rolls. 3 (Full): Gain a +2 bonus to your damage thresholds. 4 (Waning): Gain a +1 bonus to your Evasion.' },
        ],
      },
    },
    backgroundQuestions: [
      "How did you first discover your affinity for magical craft?",
      "You once used your power to help someone in a dire situation. Who were they and why did they come to you?",
      "Your magic once opened a door best left closed. Who or what was on the other side?",
    ],
    connectionQuestions: [
      "What about my magical practice makes you most ill at ease?",
      "I once appeared to you in a dream and shared a vision of the future. What did I tell you?",
      "Why do you come to me for advice?",
    ],
  },
  ...hfClasses,
];
